// src/encounter/services/use-encounter-invoice.ts
// The encounter's invoice, for the Wrap-Up invoice section.
//
// FLUTTER PATH (wrap_up_billing_screen.dart:96-97): the wrap-up screen already HAS the
// appointment in bloc state, so it can read `appointment.invoice.id` directly and fire
// `FetchInvoiceByIdEvent(appointment?.invoice?.id, false)`.
//
// React's encounter shell is routed by `appointmentId` alone and never loads the appointment
// body, so the id has to be resolved first. Two steps, both on shipped endpoints:
//   1. POST /api/v1/Practice/PreLoadAppointmentDetails with `billing: {}` — the documented
//      "load invoice" switch on that request (PreLoadAppointmentRequest) — which returns
//      `appointment.invoice.id`. Same call the reschedule flow already makes.
//   2. GET /Billing/Invoice/{id} via the shipped `useInvoice` hook.
//
// Fails soft at every step: no ids → no fetch; preload without an invoice → `invoiceId` null
// and no second call; either query failing → `invoice` undefined. The section then renders
// Flutter's own "Invoice not created" state rather than erroring the wrap-up step.
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { useInvoice } from '@/billing/services/use-invoice';
import type {
  PreLoadAppointmentRequest,
  PreLoadAppointmentResponse,
} from '@/types/scheduling-api';
import type { InvoiceDetail } from '@/shared/types/billing';

export interface UseEncounterInvoiceParams {
  appointmentId?: string | null;
  patientId?: string | null;
  providerId?: string | null;
  /** Master switch — the page passes `Show_Billing_Summary && !isNurse` (Flutter's own gate,
   *  wrap_up_billing_screen.dart:680-686). False → zero network calls. */
  enabled?: boolean;
}

export interface UseEncounterInvoiceResult {
  invoice: InvoiceDetail | undefined;
  invoiceId: string | null;
  isLoading: boolean;
  isError: boolean;
}

export function useEncounterInvoice({
  appointmentId,
  patientId,
  providerId,
  enabled = true,
}: UseEncounterInvoiceParams): UseEncounterInvoiceResult {
  const canPreload = !!enabled && !!appointmentId && !!patientId && !!providerId;

  const preload = useQuery({
    queryKey: ['encounter-invoice', 'preload', appointmentId, patientId, providerId],
    enabled: canPreload,
    // The appointment body is stable within a visit — don't refetch it on every focus.
    staleTime: 5 * 60_000,
    queryFn: ({ signal }) =>
      post<PreLoadAppointmentResponse>(
        '/api/v1/Practice/PreLoadAppointmentDetails',
        {
          appointmentId: appointmentId!,
          patientId: patientId!,
          providerId: providerId!,
          // `{}` loads the invoice, `null` skips it (PreLoadAppointmentRequest field docs).
          billing: {},
          // The patient block is already loaded by the shell — skip it.
          patient: null,
          // `appointmentProfileId` is deliberately ABSENT, not empty.
          //
          // It previously sent `''` on the belief the backend treated it as optional.
          // It does not: the field binds to a non-nullable `Guid`
          // (PreLoadAppointmentDetails.cs:57), Newtonsoft cannot convert `''`, and the
          // throw aborts binding for the whole object — so every call from this hook
          // returned 400 with two errors, the second ("The request field is required")
          // being a symptom of the first. The wrap-up invoice section therefore always
          // rendered its "Invoice not created" fallback, which fails soft and looks
          // like an appointment with no invoice rather than a broken request.
          //
          // Omitting the key lets it arrive as `Guid.Empty`, which is what "no profile
          // selected" means here. `null` fails exactly as `''` does — omission is the
          // only representation of absent that this endpoint accepts.
        } as PreLoadAppointmentRequest,
        { signal },
      ),
  });

  const invoiceId = preload.data?.appointment?.invoice?.id ?? null;

  const invoiceQuery = useInvoice({ invoiceId: canPreload && invoiceId ? invoiceId : undefined });

  return {
    invoice: invoiceQuery.data,
    invoiceId,
    isLoading: (canPreload && preload.isLoading) || (!!invoiceId && invoiceQuery.isLoading),
    isError: preload.isError || invoiceQuery.isError,
  };
}
